import React from "react";
import { useNavigate } from "react-router-dom";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  Flag,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "../../../shared/ui";
import { TransactionStatusBadge } from "../../transactions/components/TransactionStatusBadge";
import { normalizeTransactionStatus } from "../../transactions/utils/statusLookup";
import { OtcRequestItem } from "../types";

export interface OtcRequestTopSectionProps {
  request: OtcRequestItem;
  canGoPrevious: boolean;
  canGoNext: boolean;
  onNavigateSibling: (delta: number) => void;
  onReject: () => void;
  onSendOffer: () => void;
  onApprove: () => void;
  onCancelOffer: () => void;
  onUpdateStatus: () => void;
  onEscalate?: () => void;
  onFlagRequest?: () => void;
  onOpenActivityLog?: () => void;
  onBack?: () => void;
  onCompleteTransfer?: () => void;
  onDeclineTransfer?: () => void;
  onApproveTransfer?: () => void;
  isAdminExternalPayout?: boolean;
}

export const OtcRequestTopSection: React.FC<OtcRequestTopSectionProps> = ({
  request,
  canGoPrevious,
  canGoNext,
  onNavigateSibling,
  onReject,
  onSendOffer,
  onApprove,
  onCancelOffer,
  onUpdateStatus,
  onEscalate,
  onFlagRequest,
  onOpenActivityLog,
  onBack,
  onCompleteTransfer,
  onDeclineTransfer,
  onApproveTransfer,
  isAdminExternalPayout,
}) => {
  const isExternalPayout = Boolean(
    isAdminExternalPayout ?? request.isAdminExternalPayout
  );
  const navigate = useNavigate();

  const currency = (request.currency || "USD").toUpperCase();
  const absAmount = Math.abs(Number(request.amount) || 0);
  const formattedNum = absAmount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  let symbol = "";
  if (currency === "USD") symbol = "$";
  else if (currency === "GBP") symbol = "£";
  else if (currency === "EUR") symbol = "€";
  else if (currency === "NGN") symbol = "₦";

  const normState = normalizeTransactionStatus(request.state);

  const isUnderReview =
    normState === "under_review" ||
    normState === "submitted" ||
    normState === "pending_review" ||
    normState === "new";

  const isOfferSent = normState === "offer_sent" || normState === "pending";

  const isTransferExecution =
    normState === "request_approved" ||
    normState === "approved" ||
    normState === "offer_accepted" ||
    normState === "agreed" ||
    normState === "awaiting_update" ||
    normState === "awaiting_funding" ||
    normState === "processing";

  const isNeedsApproval =
    normState === "needs_approval" ||
    normState === "submitted_for_approval";

  const isFlagged =
    normState === "flagged" || request.state === "Flagged";

  return (
    <div className="flex flex-col gap-4">
      {/* Top navigation row: Back button on left, Sibling navigation on right */}
      <div className="flex w-full items-center justify-between">
        <button
          type="button"
          onClick={onBack ? onBack : () => navigate(-1)}
          className="inline-flex h-10 items-center gap-2 text-[13px] font-medium text-brand transition-colors hover:text-brand-dark"
        >
          <ArrowLeft size={16} />
          <span>Back</span>
        </button>

        {/* Sibling navigation chevrons */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onNavigateSibling(-1)}
            disabled={!canGoPrevious}
            className="flex h-10 w-10 items-center justify-center rounded-[8px] border border-neutral-200 bg-white text-neutral-700 shadow-sm transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
            title="Previous request"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => onNavigateSibling(1)}
            disabled={!canGoNext}
            className="flex h-10 w-10 items-center justify-center rounded-[8px] border border-neutral-200 bg-white text-neutral-700 shadow-sm transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
            title="Next request"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Main Header Row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-[13px] font-normal text-neutral-500">
            OTC Request
          </span>
          <div className="flex items-center gap-3">
            <h1 className="text-[28px] font-bold tracking-tight text-neutral-900">
              {symbol}
              {formattedNum}
              <span className="ml-2 text-[20px] font-normal text-neutral-500">
                {currency}
              </span>
            </h1>
            <TransactionStatusBadge status={request.state} />
          </div>
        </div>

        {/* State-driven action buttons and overflow dropdown */}
        <div className="flex flex-wrap items-center gap-3">
          {/* 1. Under review state */}
          {isUnderReview && (
            <>
              <button
                type="button"
                onClick={onReject}
                className="h-10 rounded-[8px] px-3 py-2 text-[14px] font-medium text-neutral-700 transition-colors hover:text-danger-700"
              >
                Reject
              </button>
              <Button
                type="button"
                variant="outline"
                onClick={onSendOffer}
                className="h-10 rounded-[8px] border-neutral-300 bg-surface-input px-5 text-[14px] font-medium text-neutral-900 hover:bg-surface-inputHover"
              >
                Send offer
              </Button>
              <Button
                type="button"
                onClick={onApprove}
                className="h-10 rounded-[8px] bg-button-dark px-6 text-[14px] font-medium text-white hover:bg-button-darkHover disabled:bg-button-disabled disabled:text-white disabled:cursor-not-allowed"
              >
                Approve
              </Button>
            </>
          )}

          {/* 2. Offer sent state */}
          {isOfferSent && (
            <button
              type="button"
              onClick={onCancelOffer}
              className="h-10 rounded-[8px] px-3 py-2 text-[14px] font-medium text-neutral-700 transition-colors hover:text-danger-700"
            >
              Cancel offer
            </button>
          )}

          {/* 3. Approved / Processing / Offer accepted / Awaiting update / Agreed */}
          {isTransferExecution && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={onUpdateStatus}
                className="h-10 rounded-[8px] border-neutral-300 bg-surface-input px-5 text-[14px] font-medium text-neutral-900 hover:bg-surface-inputHover"
              >
                Update status
              </Button>
              {!isExternalPayout && (
                <Button
                  type="button"
                  onClick={onCompleteTransfer || onUpdateStatus}
                  className="h-10 rounded-[8px] bg-button-dark px-6 text-[14px] font-medium text-white hover:bg-button-darkHover disabled:bg-button-disabled disabled:text-white disabled:cursor-not-allowed"
                >
                  Complete transfer
                </Button>
              )}
            </>
          )}

          {/* 4. Needs approval state (from Figma design) */}
          {isNeedsApproval && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={onDeclineTransfer || onReject}
                className="h-10 rounded-[8px] border-neutral-300 bg-white px-5 text-[14px] font-medium text-neutral-700 shadow-xs hover:bg-neutral-50 hover:text-danger-700"
              >
                Decline
              </Button>
              <Button
                type="button"
                onClick={onApproveTransfer || onUpdateStatus}
                className="h-10 rounded-[8px] bg-button-dark px-6 text-[14px] font-medium text-white shadow-xs hover:bg-button-darkHover disabled:bg-button-disabled disabled:text-white disabled:cursor-not-allowed"
              >
                Approve transfer
              </Button>
            </>
          )}

          {/* 5. Terminal / Closed / Failed / Completed states show no top action buttons (only the '...' more icon) */}

          {/* Overflow "..." dropdown (OTC specific) */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                aria-label="More actions"
                className="flex h-10 w-10 items-center justify-center rounded-[8px] border border-neutral-200 bg-white text-neutral-700 shadow-sm transition-colors hover:bg-neutral-50 hover:text-neutral-900"
              >
                <MoreHorizontal size={20} />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={8}
                className="z-50 min-w-[200px] overflow-hidden rounded-xl border border-primary-60 bg-white p-1.5 shadow-[0_12px_28px_rgba(16,24,40,0.14)] animate-in fade-in-0 zoom-in-95"
              >
                <DropdownMenu.Item
                  disabled={isFlagged}
                  onClick={isFlagged ? undefined : (onFlagRequest || onEscalate)}
                  className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium outline-none transition-colors ${
                    isFlagged
                      ? "cursor-not-allowed opacity-50 text-neutral-400"
                      : "cursor-pointer text-amber-700 hover:bg-amber-50"
                  }`}
                >
                  <Flag size={16} className={isFlagged ? "text-neutral-400" : "text-amber-700"} />
                  <span>Flag request</span>
                </DropdownMenu.Item>

                <DropdownMenu.Item
                  disabled
                  className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium text-neutral-400 outline-none opacity-50 cursor-not-allowed"
                >
                  <Download size={16} className="text-neutral-400" />
                  <span>Download summary</span>
                </DropdownMenu.Item>

                {onOpenActivityLog && (
                  <DropdownMenu.Item
                    onClick={onOpenActivityLog}
                    className="flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium text-neutral-700 outline-none transition-colors hover:bg-brand-light hover:text-neutral-900"
                  >
                    <span>Activity log</span>
                  </DropdownMenu.Item>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
    </div>
  );
};
